// <destination-autocomplete> Web Component
// Handles destination search input with autocomplete dropdown and clear button.
// Usage: <destination-autocomplete value="Paris" destination-id="-1456928" destination-type="city"
//          latitude="48.85" longitude="2.35" placeholder="Where are you going?"
//          name="destination"></destination-autocomplete>

// Read the user's preferred language for cache-key uniformity across calls.
// /api/destinations is public-cacheable per (q, lang) — appending lang to the
// URL makes the CDN key correct without Vary on Accept-Language or cookies.
// Fixed default "en-us" (matches the server's default when ?lang= is missing).
// M7 redesign §3.4.
//
// If the cookie has a stale value the M7 server doesn't recognize, the
// API returns 400 — autocomplete then falls back to "no results" via
// the fetch error path. Not catastrophic (matches the pre-cookie graceful
// degradation) but worth flagging: the cookie's lang values are managed
// by <lang-switcher> which only writes server-supported codes, so the
// edge case here is "user upgraded across a release that dropped a
// previously-supported locale" — extremely rare.
function _preferredLangForFetch() {
    try {
        const ls = localStorage.getItem('preferred_language');
        if (ls) return ls.toLowerCase();
    } catch (e) {}
    const m = document.cookie.match(/(?:^|;\s*)preferred_language=([^;]+)/);
    if (m) {
        try { return decodeURIComponent(m[1]).toLowerCase(); }
        catch (e) { return m[1].toLowerCase(); }
    }
    return 'en-us';
}

class DestinationAutocomplete extends HTMLElement {
    connectedCallback() {
        this._debounceTimer = null;
        this._trending = null;
        this._trendingPromise = null;
        this._render();
        this._wire();
    }

    static get observedAttributes() { return ['value']; }

    attributeChangedCallback(name, oldVal, newVal) {
        if (name === 'value') {
            const input = this.querySelector('.da-input');
            if (input && input.value !== newVal) {
                input.value = newVal || '';
                this._toggleClear();
            }
        }
    }

    get value() { return this.getAttribute('value') || ''; }
    set value(v) { this.setAttribute('value', v); }

    get destinationId() { return this.getAttribute('destination-id') || ''; }
    set destinationId(v) { this.setAttribute('destination-id', v); }

    get destinationType() { return this.getAttribute('destination-type') || ''; }
    set destinationType(v) { this.setAttribute('destination-type', v); }

    get latitude() { return this.getAttribute('latitude') || ''; }
    set latitude(v) { this.setAttribute('latitude', v); }

    get longitude() { return this.getAttribute('longitude') || ''; }
    set longitude(v) { this.setAttribute('longitude', v); }

    _render() {
        const name = this.getAttribute('name') || 'destination';
        const placeholder = this.getAttribute('placeholder') || 'Where are you going?';
        const value = this.value;

        this.innerHTML = `
            <div class="flex-1 bg-white rounded flex items-center px-3 py-2.5 relative">
                <svg class="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
                <input type="text" name="${name}" value="${this._esc(value)}" placeholder="${this._esc(placeholder)}"
                       class="da-input w-full outline-none text-sm font-medium text-gray-900 placeholder-gray-400" autocomplete="off">
                <button type="button" class="da-clear ${value ? 'flex' : 'hidden'} ml-1 p-0.5 text-gray-400 hover:text-gray-600 flex-shrink-0" title="Clear">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
                <input type="hidden" name="destination_id" value="${this._esc(this.destinationId)}" class="da-id">
                <input type="hidden" name="destination_type" value="${this._esc(this.destinationType)}" class="da-type">
                <input type="hidden" name="latitude" value="${this._esc(this.latitude)}" class="da-lat">
                <input type="hidden" name="longitude" value="${this._esc(this.longitude)}" class="da-lng">
                <div class="da-dropdown hidden absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border max-h-80 overflow-y-auto z-50 w-full text-gray-900"></div>
            </div>`;
    }

    _wire() {
        const input = this.querySelector('.da-input');
        const clearBtn = this.querySelector('.da-clear');
        const dropdown = this.querySelector('.da-dropdown');

        input.addEventListener('input', () => {
            clearTimeout(this._debounceTimer);
            const query = input.value.trim();
            this._toggleClear();
            // Typing dismisses the empty-submit error and re-enables the dropdown.
            this._hideError();

            if (query.length === 0) {
                this._showTrendingDropdown();
                return;
            }
            if (query.length < 2) {
                dropdown.classList.add('hidden');
                return;
            }

            this._debounceTimer = setTimeout(() => this._fetch(query), 300);
        });

        input.addEventListener('focus', () => {
            if (this._isMobile()) {
                input.blur();
                this._openOverlay();
                return;
            }
            if (input.value.length >= 2) {
                dropdown.classList.remove('hidden');
                return;
            }
            this._showTrendingDropdown();
        });

        // On mobile, intercept the tap before focus to avoid the keyboard briefly flashing.
        input.addEventListener('mousedown', (e) => {
            if (this._isMobile()) {
                e.preventDefault();
                this._openOverlay();
            }
        });

        clearBtn.addEventListener('click', () => {
            input.value = '';
            input.focus();
            this._setHidden('', '', '', '');
            this._toggleClear();
            this._hideError();
            this._dispatch();
            this._showTrendingDropdown();
        });

        document.addEventListener('click', (e) => {
            if (!this.contains(e.target)) dropdown.classList.add('hidden');
        });

        // On submit: empty destination → show inline error (booking.com pattern);
        // non-empty but no matched ID → auto-resolve via first autocomplete result.
        const form = this.closest('form');
        if (form) {
            form.addEventListener('submit', (e) => {
                const query = input.value.trim();
                const idField = this.querySelector('.da-id');
                if (!query) {
                    e.preventDefault();
                    this._showError('Please enter a destination where you\'d like to stay');
                    input.focus();
                    return;
                }
                if (!idField.value) {
                    e.preventDefault();
                    this._autoResolve(query, form);
                }
            });
        }
    }

    _showError(msg) {
        const container = this.querySelector('.da-input').parentElement;
        if (!container) return;
        container.classList.add('ring-2', 'ring-danger');
        // Position error ABOVE the input so it doesn't fight the dropdown for the
        // top-full slot. Both can be visible simultaneously (error tells the user
        // what's wrong; trending below gives them a quick way to fix it).
        let err = this.querySelector('.da-error');
        if (!err) {
            err = document.createElement('div');
            err.className = 'da-error absolute bottom-full left-0 mb-2 px-3 py-2 bg-danger text-white text-xs font-medium rounded shadow-lg z-50';
            err.innerHTML = `<span class="absolute -bottom-1.5 left-6 w-3 h-3 rotate-45 bg-danger"></span><span class="relative">${this._esc(msg)}</span>`;
            container.appendChild(err);
        } else {
            err.querySelector('.relative').textContent = msg;
            err.classList.remove('hidden');
        }
    }

    _hideError() {
        const container = this.querySelector('.da-input').parentElement;
        if (container) container.classList.remove('ring-2', 'ring-danger');
        const err = this.querySelector('.da-error');
        if (err) err.remove();
    }

    async _autoResolve(query, form) {
        try {
            const res = await fetch(`/api/destinations?q=${encodeURIComponent(query)}&lang=${encodeURIComponent(_preferredLangForFetch())}`);
            const data = await res.json();
            if (data && data.length > 0) {
                const d = data[0];
                const input = this.querySelector('.da-input');
                input.value = d.name;
                this._setHidden(d.id, d.type || 'city',
                    d.coordinates ? d.coordinates.lat : '',
                    d.coordinates ? d.coordinates.lng : '');
                this._toggleClear();
                this._dispatch();
            }
        } catch (err) {
            console.error('Auto-resolve error:', err);
        }
        // Submit the form (bypasses submit event to avoid re-triggering)
        form.submit();
    }

    async _fetch(query) {
        const dropdown = this.querySelector('.da-dropdown');
        try {
            const res = await fetch(`/api/destinations?q=${encodeURIComponent(query)}&lang=${encodeURIComponent(_preferredLangForFetch())}`);
            const data = await res.json();
            if (data.error || !data.length) { dropdown.classList.add('hidden'); return; }

            dropdown.innerHTML = data.map(d => {
                const icon = this._icon(d.type);
                const subtitle = d.subtitle || d.country || '';
                const lat = d.coordinates ? d.coordinates.lat : '';
                const lng = d.coordinates ? d.coordinates.lng : '';
                return `<div class="da-item px-4 py-3 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                             data-name="${this._esc(d.name)}" data-id="${this._esc(d.id)}"
                             data-type="${this._esc(d.type || 'city')}" data-lat="${lat}" data-lng="${lng}">
                    ${icon}
                    <div class="min-w-0">
                        <div class="font-medium text-gray-900 truncate">${this._esc(d.name)}</div>
                        <div class="text-sm text-gray-500 truncate">${this._esc(subtitle)}</div>
                    </div>
                </div>`;
            }).join('');
            dropdown.classList.remove('hidden');

            // Bind click on items
            dropdown.querySelectorAll('.da-item').forEach(item => {
                item.addEventListener('click', () => this._select(item));
            });
        } catch (err) {
            console.error('Autocomplete error:', err);
            dropdown.classList.add('hidden');
        }
    }

    _loadTrending() {
        if (this._trending) return Promise.resolve(this._trending);
        if (this._trendingPromise) return this._trendingPromise;
        // Append &lang= so the GeoIP-redirect server-side handler forwards it
        // to the canonical /api/destinations/trending/{country}?lang=... URL
        // (cacheable per-country-per-lang). Without lang, non-en users would
        // always get English trending destinations.
        this._trendingPromise = fetch('/api/destinations/trending?lang=' + encodeURIComponent(_preferredLangForFetch()))
            .then(res => res.ok ? res.json() : [])
            .then(data => {
                this._trending = Array.isArray(data) ? data : [];
                return this._trending;
            })
            .catch(() => {
                this._trendingPromise = null;
                return [];
            });
        return this._trendingPromise;
    }

    _trendingHeaderHTML(itemContainerSelector) {
        const label = this.getAttribute('trending-label') || 'Trending destinations';
        return `<div class="${itemContainerSelector === '.da-dropdown' ? 'px-4 pt-3 pb-2' : 'px-4 pt-4 pb-2'} text-xs font-bold uppercase tracking-wide text-gray-700">${this._esc(label)}</div>`;
    }

    _trendingItemsHTML(items, itemClass, extraClasses) {
        return items.map(d => {
            const icon = this._icon(d.type);
            const subtitle = d.subtitle || d.country || '';
            const lat = d.coordinates ? d.coordinates.lat : '';
            const lng = d.coordinates ? d.coordinates.lng : '';
            return `<div class="${itemClass} ${extraClasses} cursor-pointer flex items-center gap-3"
                         data-name="${this._esc(d.name)}" data-id="${this._esc(d.id)}"
                         data-type="${this._esc(d.type || 'city')}" data-lat="${lat}" data-lng="${lng}">
                ${icon}
                <div class="min-w-0">
                    <div class="font-medium text-gray-900 truncate">${this._esc(d.name)}</div>
                    <div class="text-sm text-gray-500 truncate">${this._esc(subtitle)}</div>
                </div>
            </div>`;
        }).join('');
    }

    async _showTrendingDropdown() {
        const dropdown = this.querySelector('.da-dropdown');
        if (!dropdown) return;
        const items = await this._loadTrending();
        if (!items.length) return;
        // Bail if the user started typing while we were fetching.
        const input = this.querySelector('.da-input');
        if (input && input.value.trim().length >= 2) return;
        dropdown.innerHTML =
            this._trendingHeaderHTML('.da-dropdown') +
            this._trendingItemsHTML(items, 'da-item', 'px-4 py-3 hover:bg-gray-100');
        dropdown.classList.remove('hidden');
        dropdown.querySelectorAll('.da-item').forEach(item => {
            item.addEventListener('click', () => this._select(item));
        });
    }

    _select(item) {
        const name = item.dataset.name;
        const id = item.dataset.id;
        const type = item.dataset.type;
        const lat = item.dataset.lat;
        const lng = item.dataset.lng;

        this.querySelector('.da-input').value = name;
        this._setHidden(id, type, lat, lng);
        this.querySelector('.da-dropdown').classList.add('hidden');
        this._toggleClear();
        this._hideError();
        this._dispatch();
    }

    _setHidden(id, type, lat, lng) {
        this.querySelector('.da-id').value = id;
        this.querySelector('.da-type').value = type;
        this.querySelector('.da-lat').value = lat;
        this.querySelector('.da-lng').value = lng;
    }

    _toggleClear() {
        const input = this.querySelector('.da-input');
        const btn = this.querySelector('.da-clear');
        if (!input || !btn) return;
        if (input.value.trim()) {
            btn.classList.remove('hidden');
            btn.classList.add('flex');
        } else {
            btn.classList.add('hidden');
            btn.classList.remove('flex');
        }
    }

    _dispatch() {
        const input = this.querySelector('.da-input');
        this.dispatchEvent(new CustomEvent('destination-change', {
            bubbles: true,
            detail: {
                destination: input ? input.value : '',
                destination_id: this.querySelector('.da-id')?.value || '',
                destination_type: this.querySelector('.da-type')?.value || '',
                latitude: this.querySelector('.da-lat')?.value || '',
                longitude: this.querySelector('.da-lng')?.value || '',
            }
        }));
    }

    _icon(type) {
        const upper = (type || '').toUpperCase();
        const cls = 'w-10 h-10 p-2 text-gray-500 bg-gray-100 rounded flex-shrink-0';
        if (upper === 'AIRPORT') return `<svg class="${cls}" fill="currentColor" viewBox="0 0 24 24"><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>`;
        if (upper === 'HOTEL' || upper === 'ACCOMMODATION') return `<svg class="${cls}" fill="currentColor" viewBox="0 0 24 24"><path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z"/></svg>`;
        return `<svg class="${cls}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>`;
    }

    _isMobile() {
        return window.matchMedia('(max-width: 767px)').matches;
    }

    _openOverlay() {
        if (this._overlay) return;
        // Mobile overlay covers the page — clear any submit-empty error so
        // the user isn't left with a stale red banner under the overlay.
        this._hideError();

        const placeholder = this.getAttribute('placeholder') || 'Where are you going?';
        const currentValue = this.querySelector('.da-input').value;

        const overlay = document.createElement('div');
        overlay.className = 'da-overlay fixed inset-0 z-[100] bg-white flex flex-col';
        overlay.innerHTML = `
            <div class="flex items-center gap-2 px-2 py-2 border-b border-gray-200 bg-white">
                <button type="button" class="da-ov-close p-2 text-gray-700 flex-shrink-0" aria-label="Close">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                    </svg>
                </button>
                <div class="flex-1 flex items-center bg-gray-100 rounded-lg px-3 py-2">
                    <svg class="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    <input type="text" class="da-ov-input w-full outline-none bg-transparent text-base text-gray-900 placeholder-gray-400"
                           placeholder="${this._esc(placeholder)}" autocomplete="off" value="${this._esc(currentValue)}">
                    <button type="button" class="da-ov-clear ${currentValue ? 'flex' : 'hidden'} items-center p-1 text-gray-400 flex-shrink-0" aria-label="Clear">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>
                </div>
            </div>
            <div class="da-ov-list flex-1 overflow-y-auto"></div>
        `;

        document.body.appendChild(overlay);
        document.body.style.overflow = 'hidden';
        this._overlay = overlay;

        const ovInput = overlay.querySelector('.da-ov-input');
        const ovList = overlay.querySelector('.da-ov-list');
        const ovClose = overlay.querySelector('.da-ov-close');
        const ovClear = overlay.querySelector('.da-ov-clear');

        setTimeout(() => { ovInput.focus(); ovInput.select(); }, 50);

        const toggleOvClear = () => {
            if (ovInput.value.trim()) {
                ovClear.classList.remove('hidden');
                ovClear.classList.add('flex');
            } else {
                ovClear.classList.add('hidden');
                ovClear.classList.remove('flex');
            }
        };

        const renderHint = (text) => {
            ovList.innerHTML = `<div class="p-6 text-sm text-gray-400 text-center">${this._esc(text)}</div>`;
        };

        const renderTrendingOrHint = async (fallbackText) => {
            const items = await this._loadTrending();
            if (!items.length) { renderHint(fallbackText); return; }
            // Don't clobber typed-search results that arrived first.
            if (ovInput.value.trim().length >= 2) return;
            ovList.innerHTML =
                this._trendingHeaderHTML('.da-ov-list') +
                this._trendingItemsHTML(items, 'da-ov-item', 'px-4 py-3 border-b border-gray-100 hover:bg-gray-50');
            ovList.querySelectorAll('.da-ov-item').forEach(item => {
                item.addEventListener('click', () => {
                    this._select(item);
                    this._closeOverlay();
                });
            });
        };

        const fetchResults = async (query) => {
            try {
                const res = await fetch(`/api/destinations?q=${encodeURIComponent(query)}&lang=${encodeURIComponent(_preferredLangForFetch())}`);
                const data = await res.json();
                if (data.error || !data.length) { renderHint('No results'); return; }

                ovList.innerHTML = data.map(d => {
                    const icon = this._icon(d.type);
                    const subtitle = d.subtitle || d.country || '';
                    const lat = d.coordinates ? d.coordinates.lat : '';
                    const lng = d.coordinates ? d.coordinates.lng : '';
                    return `<div class="da-ov-item px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                                 data-name="${this._esc(d.name)}" data-id="${this._esc(d.id)}"
                                 data-type="${this._esc(d.type || 'city')}" data-lat="${lat}" data-lng="${lng}">
                        ${icon}
                        <div class="min-w-0 flex-1">
                            <div class="font-medium text-gray-900 truncate">${this._esc(d.name)}</div>
                            <div class="text-sm text-gray-500 truncate">${this._esc(subtitle)}</div>
                        </div>
                    </div>`;
                }).join('');
                ovList.querySelectorAll('.da-ov-item').forEach(item => {
                    item.addEventListener('click', () => {
                        this._select(item);
                        this._closeOverlay();
                    });
                });
            } catch (err) {
                console.error('Autocomplete error:', err);
                renderHint('Error loading results');
            }
        };

        if (currentValue && currentValue.trim().length >= 2) {
            fetchResults(currentValue.trim());
        } else {
            renderTrendingOrHint('Start typing to search destinations');
        }

        let debounce;
        ovInput.addEventListener('input', () => {
            toggleOvClear();
            clearTimeout(debounce);
            const q = ovInput.value.trim();
            if (q.length < 2) { renderTrendingOrHint('Start typing to search destinations'); return; }
            debounce = setTimeout(() => fetchResults(q), 300);
        });

        ovClear.addEventListener('click', () => {
            ovInput.value = '';
            ovInput.focus();
            toggleOvClear();
            renderTrendingOrHint('Start typing to search destinations');
        });

        ovClose.addEventListener('click', () => this._closeOverlay());
    }

    _closeOverlay() {
        if (!this._overlay) return;
        this._overlay.remove();
        this._overlay = null;
        document.body.style.overflow = '';
    }

    _esc(text) {
        if (!text) return '';
        const d = document.createElement('div');
        d.textContent = text;
        return d.innerHTML;
    }
}

customElements.define('destination-autocomplete', DestinationAutocomplete);
